import { Dispatch, SetStateAction } from 'react';
import { ace_index } from '@axo/shared/data-access/types';
import { useBrands } from '@axo/shared/data-access/hooks/services/brand/useBrands';
import { MarketCountryCode } from '@axo/shared/types';
import { MultiFilterSelect } from '../MultiFilterSelect';
import { setPageNumber } from '../SearchResults';

interface Props {
  query: ace_index.FindInput;
  setQuery: Dispatch<SetStateAction<ace_index.FindInput>>;
  marketCountry: string;
  editable?: boolean;
}

type Option = { label: string; value: string };

export function Brand({
  query,
  setQuery,
  marketCountry,
  editable = false,
}: Props) {
  const brandsRaw = useBrands(marketCountry as MarketCountryCode);

  const options: Option[] = (brandsRaw.data?.Items ?? []).map((brand) => ({
    label: brand.Label,
    value: brand.Name,
  }));

  const selected = getSelectedBrands(query);
  const values: Option[] = selected.map(
    (value) =>
      options.find((option) => option.value === value) ?? {
        label: value,
        value,
      }
  );

  return (
    <MultiFilterSelect
      items={options}
      onSelect={(option) => setQuery(toggleBrand(option.value))}
      onRemove={(_, i) => setQuery(removeBrand(values[i].value))}
      stringify={(option) => option.label}
      values={values}
      label="Brand"
      description="Filter applications by brand"
      disabled={!editable}
    />
  );
}

function toggleBrand(brand: string): SetStateAction<ace_index.FindInput> {
  return (query) => {
    const current = getSelectedBrands(query);
    const next = current.includes(brand)
      ? current.filter((b) => b !== brand)
      : [...current, brand];

    return {
      ...setPageNumber(1)(query),
      LoanApplicationBrandIn: next.length > 0 ? next : undefined,
    };
  };
}

function removeBrand(brand: string): SetStateAction<ace_index.FindInput> {
  return (query) => {
    const next = getSelectedBrands(query).filter((b) => b !== brand);

    return {
      ...setPageNumber(1)(query),
      LoanApplicationBrandIn: next.length > 0 ? next : undefined,
    };
  };
}

function getSelectedBrands(query: ace_index.FindInput): string[] {
  return query.LoanApplicationBrandIn ?? [];
}
